import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { useEffect } from 'react';
import type { GroupBy, NewAccountsReportItem } from '@/models/report/types';
import type { Country } from '@/models/country/types';
import { fetchNewAccountsReport } from '@/models/report/service';
import { lastMonthToToday, extractYear } from '@/lib/dateUtils';
import { parseNumber } from '@/lib/format';
import { currencySign, groupByCountry } from '@/lib/currency';
import { countryStore } from '@/stores/country.store';

export function useNewClientsController() {
  const dates = lastMonthToToday();
  const store = useLocalObservable(() => ({
    groupBy: 'month' as GroupBy,
    dateStart: dates.start,
    dateEnd: dates.end,
    items: [] as NewAccountsReportItem[],
    isLoading: false,
    error: null as string | null,
  }));

  async function load() {
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    try {
      const items = await fetchNewAccountsReport(store.dateStart, store.dateEnd);
      runInAction(() => {
        store.items = items;
      });
    } catch (e) {
      runInAction(() => {
        store.error = (e as Error).message;
      });
    } finally {
      runInAction(() => {
        store.isLoading = false;
      });
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, []);

  function setGroupBy(value: GroupBy) {
    runInAction(() => {
      store.groupBy = value;
    });
  }
  function setDateStart(v: string) {
    runInAction(() => {
      store.dateStart = v;
    });
  }
  function setDateEnd(v: string) {
    runInAction(() => {
      store.dateEnd = v;
    });
  }

  function getGroupKey(item: NewAccountsReportItem): string {
    const year = extractYear(item.date_reg_year);
    if (store.groupBy === 'month') return `${item.date_reg_month} ${year}`;
    if (store.groupBy === 'year') return year;
    if (store.groupBy === 'country') return item.country_name;
    return item.status_name;
  }

  function grouped(): { key: string; label: string; entries: NewAccountsReportItem[] }[] {
    const map = new Map<string, NewAccountsReportItem[]>();
    for (const item of store.items) {
      const key = getGroupKey(item);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries()).map(([key, entries]) => ({ key, label: key, entries }));
  }

  function countryOf(item: NewAccountsReportItem): Country | undefined {
    return countryStore.byName.get(item.country_name);
  }

  function currencyOf(item: NewAccountsReportItem): string {
    return currencySign(countryOf(item));
  }

  function monthlyChartData(items: NewAccountsReportItem[] = store.items) {
    const map = new Map<
      string,
      { label: string; sortKey: string; clients: number; payments: number }
    >();
    for (const item of items) {
      const key = `${item.date_reg_month.slice(0, 3)} ${extractYear(item.date_reg_year)}`;
      // slice(0, 7) → 'YYYY-MM': хронологическая сортировка вместо алфавитной.
      const sortKey = (item.date_reg || '').slice(0, 7);
      const prev = map.get(key) ?? { label: key, sortKey, clients: 0, payments: 0 };
      map.set(key, {
        ...prev,
        clients: prev.clients + 1,
        payments: prev.payments + parseNumber(item.full_abon_plata),
      });
    }
    return Array.from(map.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }

  const totalClients = store.items.length;
  const activeClients = store.items.filter((client) =>
    client.status_name.toLowerCase().includes('актив')
  ).length;
  const paidClients = store.items.filter(
    (client) => client.date_payed_tariff && client.date_payed_tariff.trim()
  ).length;
  const totalCameras = store.items.reduce(
    (sum, client) => sum + parseNumber(client.count_cameras),
    0
  );
  const byCountry = groupByCountry(store.items, countryOf);
  const paymentTotals = byCountry.map((group) => ({
    ...group,
    amount: group.items.reduce((s, c) => s + parseNumber(c.full_abon_plata), 0),
  }));
  const paymentCharts = byCountry.map((group) => ({
    ...group,
    data: monthlyChartData(group.items),
  }));

  return {
    store,
    setGroupBy,
    setDateStart,
    setDateEnd,
    load,
    grouped,
    monthlyChartData,
    countryOf,
    currencyOf,
    totalClients,
    activeClients,
    paidClients,
    totalCameras,
    paymentTotals,
    paymentCharts,
  };
}
